import type { ReactNode } from 'react';
import type { PatternId } from '@/constants/categories';
// Patterns SVG monochromes — utilisés en overlay opacity 6-10% sur une affiche fallback.
// Chaque pattern utilise currentColor (crème) sur fond couleur catégorie.

const Defs = ({ id, children }: { id: string; children: ReactNode }) => (
  <defs><pattern id={id} patternUnits="userSpaceOnUse" width="64" height="64">{children}</pattern></defs>
);
const Wrap = ({ id, children }: { id: string; children: ReactNode }) => (
  <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden>
    {children}
    <rect width="100%" height="100%" fill={`url(#${id})`} />
  </svg>
);

export function Pattern({ id, opacity = 0.08 }: { id: PatternId; opacity?: number }) {
  const uid = `p-${id}`;
  const stroke = { stroke: 'currentColor', strokeWidth: 1, fill: 'none' };
  const style = { color: 'rgb(var(--paper-elev))', opacity };
  switch (id) {
    case 'parasols': // verticales rythmées (parasols de marché)
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <line x1="8" y1="0" x2="8" y2="64" {...stroke} />
            <line x1="24" y1="0" x2="24" y2="64" {...stroke} />
            <line x1="40" y1="0" x2="40" y2="64" {...stroke} />
            <line x1="56" y1="0" x2="56" y2="64" {...stroke} />
            <circle cx="8" cy="32" r="6" {...stroke} />
            <circle cx="40" cy="20" r="6" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'confetti':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <rect x="6" y="8" width="6" height="2" fill="currentColor" transform="rotate(20 9 9)" />
            <rect x="32" y="40" width="8" height="2" fill="currentColor" transform="rotate(-32 36 41)" />
            <rect x="48" y="14" width="6" height="2" fill="currentColor" transform="rotate(50 51 15)" />
            <rect x="20" y="24" width="6" height="2" fill="currentColor" transform="rotate(-12 23 25)" />
            <circle cx="10" cy="48" r="1.4" fill="currentColor" />
            <circle cx="46" cy="32" r="1.4" fill="currentColor" />
          </Defs>
        </Wrap>
      </div>;
    case 'curves':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <path d="M0,32 Q16,12 32,32 T64,32" {...stroke} />
            <path d="M0,48 Q16,28 32,48 T64,48" {...stroke} />
            <path d="M0,16 Q16,-4 32,16 T64,16" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'waves':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <path d="M0,18 Q8,10 16,18 T32,18 T48,18 T64,18" {...stroke} />
            <path d="M0,34 Q8,26 16,34 T32,34 T48,34 T64,34" {...stroke} />
            <path d="M0,50 Q8,42 16,50 T32,50 T48,50 T64,50" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'organic':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <circle cx="14" cy="14" r="6" {...stroke} />
            <circle cx="44" cy="22" r="9" {...stroke} />
            <circle cx="22" cy="44" r="7" {...stroke} />
            <circle cx="52" cy="50" r="5" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'grid':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <line x1="0" y1="16" x2="64" y2="16" {...stroke} />
            <line x1="0" y1="32" x2="64" y2="32" {...stroke} />
            <line x1="0" y1="48" x2="64" y2="48" {...stroke} />
            <line x1="16" y1="0" x2="16" y2="64" {...stroke} />
            <line x1="32" y1="0" x2="32" y2="64" {...stroke} />
            <line x1="48" y1="0" x2="48" y2="64" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'damier':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <rect x="0" y="0" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="32" y="0" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="16" y="16" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="48" y="16" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="0" y="32" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="32" y="32" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="16" y="48" width="16" height="16" fill="currentColor" opacity="0.5" />
            <rect x="48" y="48" width="16" height="16" fill="currentColor" opacity="0.5" />
          </Defs>
        </Wrap>
      </div>;
    case 'radiant':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <circle cx="32" cy="32" r="6" {...stroke} />
            <circle cx="32" cy="32" r="14" {...stroke} />
            <circle cx="32" cy="32" r="22" {...stroke} />
            <circle cx="32" cy="32" r="30" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'stars':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            {[[10,10],[40,18],[22,32],[52,42],[14,52],[46,58],[34,8]].map(([x,y],i) => (
              <g key={i} transform={`translate(${x},${y})`}>
                <line x1="-3" y1="0" x2="3" y2="0" stroke="currentColor" strokeWidth="1" />
                <line x1="0" y1="-3" x2="0" y2="3" stroke="currentColor" strokeWidth="1" />
              </g>
            ))}
          </Defs>
        </Wrap>
      </div>;
    case 'tent':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <line x1="4" y1="0" x2="4" y2="64" {...stroke} />
            <line x1="14" y1="0" x2="14" y2="64" {...stroke} />
            <line x1="24" y1="0" x2="24" y2="64" {...stroke} />
            <line x1="34" y1="0" x2="34" y2="64" {...stroke} />
            <line x1="44" y1="0" x2="44" y2="64" {...stroke} />
            <line x1="54" y1="0" x2="54" y2="64" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'eventails':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            <path d="M32,64 Q4,40 4,16" {...stroke} />
            <path d="M32,64 Q14,32 18,8" {...stroke} />
            <path d="M32,64 Q32,32 32,4" {...stroke} />
            <path d="M32,64 Q50,32 46,8" {...stroke} />
            <path d="M32,64 Q60,40 60,16" {...stroke} />
          </Defs>
        </Wrap>
      </div>;
    case 'dots':
      return <div style={style as any} className="absolute inset-0">
        <Wrap id={uid}>
          <Defs id={uid}>
            {[[8,8],[24,8],[40,8],[56,8],[16,24],[32,24],[48,24],[8,40],[24,40],[40,40],[56,40],[16,56],[32,56],[48,56]].map(([x,y],i) =>
              <circle key={i} cx={x} cy={y} r="1.6" fill="currentColor" />)}
          </Defs>
        </Wrap>
      </div>;
  }
}
